import type { FetchFunction } from 'relay-runtime';
import type { AcquireToken } from '../auth/access-token';
import type { ServiceStatusStore } from './service-status';

type RelayRequest = Parameters<FetchFunction>[0];
type RelayVariables = Parameters<FetchFunction>[1];

// Gateway responses seen while an Azure Container App scales up from zero.
const COLD_START_STATUSES = new Set([502, 503, 504]);

export interface AdminFetchDeps {
    /** Base URL of the Admin GraphQL host (no trailing slash); `/graphql` is appended. */
    apiUrl: string;
    /** Returns a fresh Entra access token for the operator. */
    acquireToken: AcquireToken;
    /** Surfaces the cold-start "waking…" state to the UI. */
    status: ServiceStatusStore;
    /** Retries while the host cold-starts (default 6 ≈ up to ~30s). */
    maxColdStartAttempts?: number;
    /** Delay between cold-start retries (default 3s). */
    coldStartDelayMs?: number;
    /** Injectable sleep for tests. */
    sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * The Admin Relay fetch. Unlike the vault fetch it injects the operator's **Entra**
 * token and sends **no** `__vault__` header (admin queries are not vault-scoped).
 * The admin host scales to zero, so the first request after idle may hit a cold
 * start — this retries on gateway/network failures, driving the shared
 * `ServiceStatusStore` so the shell can show "waking the admin service…".
 */
export function createAdminFetch(deps: AdminFetchDeps): FetchFunction {
    const {
        apiUrl,
        acquireToken,
        status,
        maxColdStartAttempts = 6,
        coldStartDelayMs = 3000,
        sleep = defaultSleep,
    } = deps;
    const endpoint = `${apiUrl}/graphql`;

    async function post(request: RelayRequest, variables: RelayVariables, token: string): Promise<Response> {
        return fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ query: request.text, variables }),
        });
    }

    const fetchFn: FetchFunction = async (request, variables) => {
        const token = await acquireToken();

        let lastError: unknown;
        for (let attempt = 0; attempt <= maxColdStartAttempts; attempt++) {
            try {
                const response = await post(request, variables, token);

                if (COLD_START_STATUSES.has(response.status) && attempt < maxColdStartAttempts) {
                    status.set('waking');
                    await sleep(coldStartDelayMs);
                    continue;
                }

                if (!response.ok) {
                    status.set(response.status >= 500 ? 'error' : 'ready');
                    throw new Error(`Admin GraphQL request failed: ${response.status} ${response.statusText}`);
                }

                status.set('ready');
                return await response.json();
            } catch (error) {
                lastError = error;
                // A rejected fetch (host asleep / connection refused) is the classic
                // cold-start signal — retry with backoff before giving up.
                const isNetworkError = error instanceof TypeError;
                if (isNetworkError && attempt < maxColdStartAttempts) {
                    status.set('waking');
                    await sleep(coldStartDelayMs);
                    continue;
                }
                if (!isNetworkError) throw error;
            }
        }

        status.set('error');
        throw lastError ?? new Error('Admin service unreachable after cold-start retries.');
    };

    return fetchFn;
}
